import { UUID } from '@cmg/common';
import React from 'react';
import { matchPath, useLocation } from 'react-router-dom';

export type OfferingSidePanelMatch = {
  /**
   * Portion of the pathname before the "/offering-side-panel/:offeringId" segment. The side panel
   * can be opened on top of many different "host" pages (calendar, my-offerings, an offering's
   * wall-cross tab, ...), so this prefix is unbounded and can't be expressed as a v6 route pattern:
   * v6 only supports a trailing splat, not the leading wildcard v5's
   * "star, offering-side-panel, colon-offeringId" pattern relied on. Used to navigate back to the
   * host page when the panel is closed.
   */
  basePathname: string;
  offeringId: UUID;
  /** Portion of the pathname after ":offeringId" (e.g. "/details", "" when at the panel root). */
  rest: string;
  /** Equivalent to v5's non-exact `useRouteMatch(...).url` - the pathname through ":offeringId". */
  url: string;
};

const OFFERING_SIDE_PANEL_PATTERN = /^(.*?)\/offering-side-panel\/([^/]+)((?:\/.*)?)$/;

/**
 * Pure pathname parser backing {@link useOfferingSidePanelMatch}, split out so the matching logic
 * can be unit tested without rendering a component.
 */
export const parseOfferingSidePanelPathname = (pathname: string): OfferingSidePanelMatch | null => {
  const match = OFFERING_SIDE_PANEL_PATTERN.exec(pathname);

  if (!match) {
    return null;
  }

  const [, basePathname, offeringId, rest] = match;

  return {
    basePathname,
    offeringId: offeringId as UUID,
    rest,
    url: `${basePathname}/offering-side-panel/${offeringId}`,
  };
};

/**
 * Several side-panel route trees (OrderBookSellSideRoute, OfferingSidePanelSellSideRoutes, ...)
 * call `useRoutes(routes, rest)` to match their v6-incompatible leading-wildcard `rest` pathname.
 * Passing that explicit `location` arg scopes `useLocation()` for everything rendered beneath
 * them to `rest` alone, which no longer contains the "/offering-side-panel/:offeringId" segment
 * this hook parses for — so a naive `useLocation()` call breaks as soon as it's nested one or
 * more of those `useRoutes(routes, rest)` levels deep. OfferingSidePanelContainer computes the
 * match once, from the real (unscoped) location, and provides it here so every descendant sees
 * the same correct value regardless of nesting depth.
 */
const OfferingSidePanelMatchContext = React.createContext<
  OfferingSidePanelMatch | null | undefined
>(undefined);
export const OfferingSidePanelMatchProvider = OfferingSidePanelMatchContext.Provider;

/**
 * Locates the "/offering-side-panel/:offeringId" segment anywhere in the current pathname.
 * OfferingSidePanelContainer mounts unconditionally at the RootRouter level (outside of
 * <Routes>), so it can't rely on route-based matching for this segment.
 */
export const useOfferingSidePanelMatch = (): OfferingSidePanelMatch | null => {
  const providedMatch = React.useContext(OfferingSidePanelMatchContext);
  const { pathname } = useLocation();
  return providedMatch !== undefined ? providedMatch : parseOfferingSidePanelPathname(pathname);
};

/**
 * The `useRoutes(routes, rest)` calls described above scope `useLocation()`'s `search` too, and
 * unlike `pathname` there's no remainder to recover it from - `rest` is parsed from `pathname`
 * alone (see `parseOfferingSidePanelPathname`), so the scoped `search` is always empty. Mirrors
 * `OfferingSidePanelMatchContext`: `OfferingSidePanelContainer` computes the real `search` once,
 * from the real (unscoped) location, and provides it here so every descendant sees the same
 * correct value regardless of nesting depth.
 */
const OfferingSidePanelSearchContext = React.createContext<string | undefined>(undefined);
export const OfferingSidePanelSearchProvider = OfferingSidePanelSearchContext.Provider;

export const useOfferingSidePanelSearch = (): string => {
  const providedSearch = React.useContext(OfferingSidePanelSearchContext);
  const { search } = useLocation();
  return providedSearch !== undefined ? providedSearch : search;
};

/**
 * Convenience hook for components mounted inside the side panel that only need the offeringId,
 * mirroring the v5 `useParams<{ offeringId: UUID }>()` call they used to make. Defaults to an
 * empty string when used outside of a side panel route so callers keep a plain `UUID` type,
 * matching the `useParams` destructuring-with-default pattern established in Phase 4b.
 */
export const useOfferingSidePanelOfferingId = (): UUID => {
  return useOfferingSidePanelMatch()?.offeringId ?? ('' as UUID);
};

/**
 * Checks a side-panel-relative routePath (see offeringSidePanelBuySideRouteFactory.ts /
 * offeringSidePanelSellSideRouteFactory.ts) against the panel-relative `rest` pathname. Mirrors
 * the v5 `!!useRouteMatch(routePath)` calls these route factories' routePaths used to support,
 * back when they were absolute (v5-only) wildcard patterns.
 */
export const useOfferingSidePanelRouteMatch = (routePath: string, end = false): boolean => {
  const match = useOfferingSidePanelMatch();
  return !!matchPath({ path: routePath, end }, match?.rest || '/');
};
